/*
 * Thermal receipts as raw ESC/POS. The receipt is drawn as a black & white
 * image and sent in the printer's own language, so no printer driver is
 * involved — a wrong or generic driver (e.g. macOS turning the job into
 * PostScript, which a receipt printer then prints as text) can't spoil it.
 * Almost every receipt printer (TVS, Epson, Xprinter, Rugtek...) speaks ESC/POS.
 *
 * Every knob comes from the printer's profile in Printer Setup
 * (print-settings.js > DEFAULT_RECEIPT_PROFILE).
 */
import html2canvas from "html2canvas";

// GS v 0 images are sent in small bands: printers with a small image buffer
// otherwise lose their place and print the rest of the image as symbols.
const RASTER_BAND_ROWS = 24;

const ESC = "\x1B";
const GS = "\x1D";

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });

// The browser paints the receipt itself (HTML inside an SVG image), so text and
// lines land exactly where the page lays them out. html2canvas re-implements
// text drawing and, on some machines, puts text a few dots lower than the page
// does, so it ran into lines and got clipped by table cells.
async function browserCanvas(doc, root, dots) {
  const rect = root.getBoundingClientRect();
  const w = rect.width;
  const h = Math.ceil(rect.height);
  const scale = dots / w;
  const clone = root.cloneNode(true);
  // an SVG image can't load anything from outside, so pictures go in as data
  await Promise.all(
    Array.from(clone.querySelectorAll("img")).map(async (img) => {
      const src = img.getAttribute("src") || "";
      if (!src || src.startsWith("data:")) return;
      try {
        img.setAttribute("src", await blobToDataUrl(await (await fetch(img.src, { mode: "cors" })).blob()));
      } catch {
        img.removeAttribute("src");
      }
    })
  );
  const css = Array.from(doc.querySelectorAll("style"))
    .map((s) => s.textContent)
    .join("\n");
  // what .zp-root inherits from the page around it
  const cs = doc.defaultView.getComputedStyle(root.parentElement || root);
  const inherit = `font-family:${cs.fontFamily};font-size:${cs.fontSize};line-height:${cs.lineHeight};color:${cs.color};`.replace(/"/g, "'");
  const xml = new XMLSerializer();
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${dots}" height="${Math.ceil(h * scale)}" viewBox="0 0 ${w} ${h}">` +
    `<foreignObject x="0" y="0" width="${w}" height="${h}">` +
    `<div xmlns="http://www.w3.org/1999/xhtml" style="width:${w}px;height:${h}px;background:#fff;${inherit}">` +
    `<style><![CDATA[${css}]]></style>` +
    xml.serializeToString(clone) +
    `</div></foreignObject></svg>`;
  const img = new Image();
  img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svg);
  await img.decode();
  const canvas = document.createElement("canvas");
  canvas.width = dots;
  canvas.height = Math.ceil(h * scale);
  const g = canvas.getContext("2d");
  g.fillStyle = "#fff";
  g.fillRect(0, 0, canvas.width, canvas.height);
  g.drawImage(img, 0, 0, canvas.width, canvas.height);
  g.getImageData(0, 0, 1, 1); // throws if the browser won't let us read it back
  return canvas;
}

// Draws the rendered receipt (.zp-root of the print page) onto a canvas exactly
// `dots` printer dots wide.
async function receiptCanvas(doc, dots) {
  const root = doc.querySelector(".zp-root");
  if (!root) throw new Error("Nothing to print (receipt did not render)");
  try {
    return await browserCanvas(doc, root, dots);
  } catch (e) {
    console.warn("Receipt: browser drawing not available, using html2canvas", e);
  }
  const cssWidth = root.getBoundingClientRect().width;
  return html2canvas(root, {
    scale: dots / cssWidth,
    backgroundColor: "#ffffff",
    useCORS: true,
    logging: false,
    windowWidth: Math.ceil(cssWidth) + 40,
  });
}

// 1 bit per dot, rows padded to whole bytes; dark = 1.
function toBitmap(canvas, widthDots, offsetDots, darkness) {
  const ctx = canvas.getContext("2d");
  const { width, height } = canvas;
  const px = ctx.getImageData(0, 0, width, height).data;
  const bytesPerRow = Math.ceil(widthDots / 8);
  const bits = new Uint8Array(bytesPerRow * height);
  for (let y = 0; y < height; y++) {
    const rowBase = y * bytesPerRow;
    for (let x = 0; x < width; x++) {
      const dx = x + offsetDots;
      if (dx < 0 || dx >= widthDots) continue;
      const i = (y * width + x) * 4;
      // luminance, transparent counts as white
      const a = px[i + 3] / 255;
      const lum = (0.299 * px[i] + 0.587 * px[i + 1] + 0.114 * px[i + 2]) * a + 255 * (1 - a);
      if (lum < darkness) bits[rowBase + (dx >> 3)] |= 0x80 >> (dx & 7);
    }
  }
  return { bits, bytesPerRow, rows: height };
}

const rowIsBlank = (bm, y) => {
  for (let i = y * bm.bytesPerRow, end = i + bm.bytesPerRow; i < end; i++) if (bm.bits[i]) return false;
  return true;
};

// Cuts blank rows off the top and bottom (if asked), then adds the wanted space.
function frameRows(bm, { trim, topRows, bottomRows }) {
  let first = 0;
  let last = bm.rows - 1;
  if (trim) {
    while (first <= last && rowIsBlank(bm, first)) first++;
    while (last >= first && rowIsBlank(bm, last)) last--;
  }
  const body = Math.max(0, last - first + 1);
  const rows = Math.max(1, topRows + body + bottomRows);
  const bits = new Uint8Array(bm.bytesPerRow * rows);
  bits.set(bm.bits.subarray(first * bm.bytesPerRow, (first + body) * bm.bytesPerRow), topRows * bm.bytesPerRow);
  return { bits, bytesPerRow: bm.bytesPerRow, rows };
}

function bytesToLatin1(bytes) {
  let s = "";
  for (let i = 0; i < bytes.length; i += 4096) s += String.fromCharCode.apply(null, bytes.subarray(i, i + 4096));
  return s;
}

// ESC * 33: 24-dot double-density bit image, printed in 24-dot strips.
// The oldest ESC/POS image command, so practically every receipt printer
// prints it. `spacing` is the paper feed between strips: 24 on most printers;
// printers that count it in a coarser unit leave thin white lines across the
// text and need a smaller number (e.g. 22 or 21).
function columnCommands({ bits, bytesPerRow, rows }, spacing) {
  const width = bytesPerRow * 8;
  let out = ESC + "3" + String.fromCharCode(spacing);
  const strip = new Uint8Array(width * 3);
  for (let y0 = 0; y0 < rows; y0 += 24) {
    strip.fill(0);
    for (let x = 0; x < width; x++) {
      const byte = x >> 3;
      const mask = 0x80 >> (x & 7);
      for (let k = 0; k < 24; k++) {
        const y = y0 + k;
        if (y < rows && bits[y * bytesPerRow + byte] & mask) strip[x * 3 + (k >> 3)] |= 0x80 >> (k & 7);
      }
    }
    out += ESC + "*\x21" + String.fromCharCode(width & 255, width >> 8) + bytesToLatin1(strip) + "\n";
  }
  return out + ESC + "2"; // back to the default line spacing
}

// GS v 0: raster bit image. The paper moves exactly by the image height, so
// the bands always touch (no white lines).
function rasterCommands({ bits, bytesPerRow, rows }) {
  let out = "";
  for (let y = 0; y < rows; y += RASTER_BAND_ROWS) {
    const h = Math.min(RASTER_BAND_ROWS, rows - y);
    out += GS + "v0\x00" + String.fromCharCode(bytesPerRow & 255, bytesPerRow >> 8, h & 255, h >> 8);
    out += bytesToLatin1(bits.subarray(y * bytesPerRow, (y + h) * bytesPerRow));
  }
  return out;
}

const CUTS = {
  partial: GS + "VB", // partial cut after feeding n
  full: GS + "VA", // full cut after feeding n
  none: "",
};

// mm from a profile field; empty means "not set"
const mmOr = (value, fallback) => (value === "" || value === null || value === undefined || isNaN(Number(value)) ? fallback : Math.max(0, Number(value)));

// The image settings the line test tries, in the order they are printed.
export const LINE_TEST_OPTIONS = [
  { id: "A", imageMode: "raster", label: "Fast (GS v 0)" },
  ...[24, 23, 22, 21, 20].map((n, i) => ({ id: String.fromCharCode(66 + i), imageMode: "column", stripSpacing: n, label: `Standard, strip spacing ${n}` })),
];

// One sample per option: a label, a solid black bar (white lines in it = gaps
// or missing rows) and a line of text with a rule just under it (the rule
// touching the text = rows lost or overlapping).
function lineTestCanvas(widthDots, option) {
  const c = document.createElement("canvas");
  c.width = widthDots;
  c.height = 168; // 7 strips of 24
  const g = c.getContext("2d");
  g.fillStyle = "#fff";
  g.fillRect(0, 0, c.width, c.height);
  g.fillStyle = "#000";
  g.textBaseline = "alphabetic";
  g.font = "bold 22px Arial, Helvetica, sans-serif";
  g.fillText(`${option.id}  ${option.label}`, 0, 24);
  g.fillRect(0, 34, widthDots, 72);
  g.font = "22px Arial, Helvetica, sans-serif";
  g.fillText("Round Off   Qty   0.00", 0, 136);
  g.fillRect(0, 144, widthDots, 2); // 8 dots (1 mm) below the text
  return c;
}

/**
 * A calibration print: the same samples sent with each image setting, so the
 * printer shows which one it prints cleanly.
 */
export function buildLineTest(profile) {
  const dpi = Number(profile.dpi) || 203;
  const dotsPerMm = dpi === 203 ? 8 : dpi / 25.4;
  const printableDots = Math.max(8, Math.floor(((Number(profile.printableWidth) || 72) * dotsPerMm) / 8) * 8);
  let out = "\x00".repeat(64) + ESC + "a\x00";
  LINE_TEST_OPTIONS.forEach((o) => {
    const bitmap = toBitmap(lineTestCanvas(printableDots, o), printableDots, 0, 128);
    out += o.imageMode === "raster" ? rasterCommands(bitmap) : columnCommands(bitmap, o.stripSpacing);
  });
  const feed = Math.max(0, Math.min(20, parseInt(profile.feedLines, 10) || 0));
  const cut = CUTS[profile.cut] === undefined ? CUTS.partial : CUTS[profile.cut];
  out += cut ? cut + String.fromCharCode(Math.min(255, (feed + 2) * 24)) : ESC + "d" + String.fromCharCode(feed + 2);
  return out + ESC + "@";
}

// The last receipt image sent to a printer, so Printer Setup can save it as a
// PNG: if the PNG is right but the paper isn't, the printer is the problem.
let lastReceipt = null;

export function lastReceiptPng() {
  if (!lastReceipt) return null;
  const { bits, bytesPerRow, rows } = lastReceipt;
  const width = bytesPerRow * 8;
  const c = document.createElement("canvas");
  c.width = width;
  c.height = rows;
  const g = c.getContext("2d");
  const img = g.createImageData(width, rows);
  for (let y = 0; y < rows; y++)
    for (let x = 0; x < width; x++) {
      const o = (y * width + x) * 4;
      const v = bits[y * bytesPerRow + (x >> 3)] & (0x80 >> (x & 7)) ? 0 : 255;
      img.data[o] = img.data[o + 1] = img.data[o + 2] = v;
      img.data[o + 3] = 255;
    }
  g.putImageData(img, 0, 0);
  return c.toDataURL("image/png");
}

/**
 * Builds the ESC/POS job for a receipt already rendered in `doc`.
 * info: from analyzePrintHtml (margins, contentWidth).
 * profile: the printer's profile from Printer Setup (print-settings.js).
 * Returns a binary string (one char = one byte).
 */
export async function buildEscposReceipt(doc, info, profile, copies = 1) {
  const dpi = Number(profile.dpi) || 203;
  // 203 dpi printers are built as exactly 8 dots/mm (576 dots on 72 mm)
  const dotsPerMm = dpi === 203 ? 8 : dpi / 25.4;
  const printableDots = Math.max(8, Math.floor(((Number(profile.printableWidth) || 72) * dotsPerMm) / 8) * 8);
  const designDots = Math.round(info.contentWidth * dotsPerMm);
  // "shrink": a format wider than the head is scaled down so nothing is cut off
  // "actual": always printed at its designed size, so fonts are exactly as set
  //           (whatever is wider than the head is cut off)
  const contentDots = profile.fit === "actual" ? designDots : Math.min(printableDots, designDots);
  const canvas = await receiptCanvas(doc, contentDots);
  // centred on the head, then the printer's own correction
  const offset = Math.round((printableDots - contentDots) / 2 + (Number(profile.offsetMm) || 0) * dotsPerMm);
  const darkness = Math.max(30, Math.min(250, Number(profile.darkness) || 150));
  const trim = profile.trimBlank !== false;
  // with trimming the format's own top/bottom margin is gone, so the default
  // space is 0; without it the format's margins are kept
  const bitmap = frameRows(toBitmap(canvas, printableDots, offset, darkness), {
    trim,
    topRows: Math.round(mmOr(profile.topSpaceMm, trim ? 0 : info.margins.top) * dotsPerMm),
    bottomRows: Math.round(mmOr(profile.bottomSpaceMm, trim ? 0 : info.margins.bottom) * dotsPerMm),
  });
  lastReceipt = bitmap;
  const spacing = Math.max(16, Math.min(30, parseInt(profile.stripSpacing, 10) || 24));
  const feed = Math.max(0, Math.min(20, parseInt(profile.feedLines, 10) || 0));
  const cut = CUTS[profile.cut] === undefined ? CUTS.partial : CUTS[profile.cut];
  // No ESC @ (reset) in front of the image: some printers (TVS, many clones)
  // throw away whatever arrived right after a reset, so the start of the image
  // was lost and the rest of that strip printed as symbols. The reset is sent
  // at the end of the job instead, and NULs (ignored by every printer) are
  // sent first as a safety margin.
  const one =
    "\x00".repeat(64) +
    ESC + "a\x00" + // left aligned (the image already carries its own centring)
    (profile.imageMode === "raster" ? rasterCommands(bitmap) : columnCommands(bitmap, spacing)) +
    // feed (n dots, ~24 per line) so the cut is below the content
    (cut ? cut + String.fromCharCode(Math.min(255, feed * 24)) : feed ? ESC + "d" + String.fromCharCode(feed) : "");
  const drawer = profile.openDrawer ? ESC + "p\x00\x19\xfa" : ""; // pulse drawer pin 2
  return one.repeat(Math.max(1, copies)) + drawer + ESC + "@"; // leave the printer in its default state
}
